import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import bannerEvoplay from "@/assets/images/BANNERS/BANNAER-EVOPLAY.jpg";
import bannerAnimalitos from "@/assets/images/BANNERS/BANNER-ANIMALITOS.jpg";
import bannerCashback from "@/assets/images/BANNERS/BANNER-CASHBACK.jpg";
import bannerDosHoras from "@/assets/images/BANNERS/BANNER-DOS-HORAS-LOCAS.jpg";
import bannerFeed from "@/assets/images/BANNERS/BANNER-INICIO-FEED.jpg";
import bannerMananas from "@/assets/images/BANNERS/BANNER-PROMO-MAÑANAS.jpg";
import bannerZelle from "@/assets/images/BANNERS/BANNER-ZELLE-Y-PAGO-MOVIL.jpg";
import bannerWildTiger from "@/assets/images/BANNERS/banner-wild-tiger.jpg";
import {SEO} from "@/components";
import {PrimaryButton} from "@/components/Button";
import GameSearchBar from "@/components/GameSearchBar";
import HomeNewsCarousel from "@/components/news/HomeNewsCarousel/HomeNewsCarousel";
import {StoriesCarousel} from "@/components/Stories";
import {GamePromos} from "@/pages/private/Games/components/List/components";
import {FavoriteGamesCarousel} from "@/pages/private/Games/components/List/components/favorite-games-carousel";
import PromoBanner from "@/pages/public/Home/components/PromoBanner/PromoBanner";
import {
  cashbackPromoTheme,
  dailySpinsPinkTheme,
  mondaySpinsPromoTheme,
  registerPromoTheme,
  thursdaySpinsRedTheme,
} from "@/pages/public/Home/components/PromoBanner/promoBannerThemes";
import {useBalanceStore} from "@/store/useBalance.store";
import {dropStorage, setStorage} from "@/store/util.store";
import {ErrorBoundaryHome} from "../../../components/ErrorBoundaryHandler/ErrorBoundaryHome";
import HeroBanner, {
  HeroAccent,
  HeroContent,
  HeroCta,
  HeroSubtitle,
  HeroTag,
  HeroTitle,
} from "../../../components/HeroBanner";
import LiveBetFeed from "../../../components/Home/LiveBetFeed/LiveBetFeed";
import LiveWinners from "../../../components/Home/LiveWinners/LiveWinners";
import TopReferrals from "../../../components/Home/TopReferrals/TopReferrals";
import {useAuthStore} from "../../../store/useAuth.store";
import {useGameMetadataStore} from "../../../store/useGameMetadataStore";
import {useMiniGames} from "../../../store/useMiniGames";
import {RecentlyPlayedGames} from "../../private/Games/components/List/components/recently-played-games";
import {TopGames} from "../../private/Games/components/List/components/top-games";
import AnimalitosExpressCarousel from "./components/AnimalitosExpressCarousel";
import AppBanners from "./components/AppBanners";
import {BreakingLeague} from "./components/BreakingLeague";
import GameCardHome from "./components/GameCardHome";
import GameCategoriesCarousel from "./components/GameCategoriesCarousel/GameCategoriesCarousel";
import MiniGamesCarousel from "./components/MiniGamesCarousel/MiniGamesCarousel";
import ModalSessionInfo from "./components/ModalSessionInfo";
import PaymentProfileCard from "./components/PaymentProfileCard";
import ThemeGamesCarousel from "./components/ThemeGamesCarousel/ThemeGamesCarousel";
import ThemesCarousel from "./components/ThemesCarousel/ThemesCarousel";
import styles from "./home.module.css";

const HOME_DISABLE_MOTION_DEBUG = true;
const VE_OFFSET = -4;
const CASHBACK_PERCENTAGE = 13;

const getNowVenezuela = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60_000;
  return new Date(utc + VE_OFFSET * 3_600_000);
};

const getNextMonday = () => {
  const ve = getNowVenezuela();
  const day = ve.getDay();
  const daysUntilMonday = day === 0 ? 1 : day === 1 ? 7 : 8 - day;
  const nextMonday = new Date(ve);
  nextMonday.setDate(ve.getDate() + daysUntilMonday);
  nextMonday.setHours(0, 0, 0, 0);
  return nextMonday.getTime();
};

const formatCountdown = ms => {
  if (ms <= 0) return {days: 0, hours: 0, minutes: 0};
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return {days, hours, minutes};
};

const getNextDailyPromoStart = veNow => {
  const nextStart = new Date(veNow);
  nextStart.setSeconds(0, 0);

  const day = veNow.getDay(); // 0: Sun ... 4: Thu
  const hour = veNow.getHours();
  const isThursday = day === 4;

  if (!isThursday && hour < 20) {
    nextStart.setHours(20, 0, 0, 0);
    return nextStart.getTime();
  }

  nextStart.setDate(nextStart.getDate() + 1);
  nextStart.setHours(20, 0, 0, 0);

  while (nextStart.getDay() === 4) {
    nextStart.setDate(nextStart.getDate() + 1);
  }

  return nextStart.getTime();
};

const getNextThursdayPromoStart = veNow => {
  const nextStart = new Date(veNow);
  nextStart.setSeconds(0, 0);

  const day = veNow.getDay(); // 4 = Thursday
  const hour = veNow.getHours();

  if (day === 4 && hour < 20) {
    nextStart.setHours(20, 0, 0, 0);
    return nextStart.getTime();
  }

  let daysUntilThursday = (4 - day + 7) % 7;
  if (daysUntilThursday === 0) {
    daysUntilThursday = 7;
  }

  nextStart.setDate(nextStart.getDate() + daysUntilThursday);
  nextStart.setHours(20, 0, 0, 0);
  return nextStart.getTime();
};

const HomePage = () => {
  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const getMiniGames = useMiniGames(state => state.getMiniGames);
  const allGames = useMiniGames(state => state.list);

  const initializeGameMetadata = useGameMetadataStore(state => state.initializeGames);
  const gamesMetadataInitialized = useGameMetadataStore(state => state.initialized);
  const gamesMetadataLoading = useGameMetadataStore(state => state.loading);

  const setStoreSearch = useMiniGames(state => state.setSearch);
  const providerOptions = useMiniGames(state => state.providerOptions);
  const classOptions = useMiniGames(state => state.classOptions);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [homeSearchTerm, setHomeSearchTerm] = useState("");
  const [homeActiveProvider, setHomeActiveProvider] = useState("");
  const [homeActiveCategory, setHomeActiveCategory] = useState("");
  const [promoCountdown, setPromoCountdown] = useState(() => formatCountdown(getNextMonday() - Date.now()));
  const [dailyPromoCountdown, setDailyPromoCountdown] = useState(() =>
    formatCountdown(getNextDailyPromoStart(getNowVenezuela()) - Date.now())
  );
  const [thursdayPromoCountdown, setThursdayPromoCountdown] = useState(() =>
    formatCountdown(getNextThursdayPromoStart(getNowVenezuela()) - Date.now())
  );
  const navigate = useNavigate();
  const sectionsRef = useRef(null);
  const atmosphereRef = useRef(null);
  const zoneRef = useRef(null);

  useEffect(() => {
    if (allGames.length === 0) {
      getMiniGames("", "");
    }
  }, [allGames.length, getMiniGames]);

  useEffect(() => {
    if (!gamesMetadataInitialized && !gamesMetadataLoading) {
      initializeGameMetadata();
    }
  }, [gamesMetadataInitialized, gamesMetadataLoading, initializeGameMetadata]);

  useEffect(() => {
    const interval = setInterval(() => {
      const ms = getNextMonday() - Date.now();
      setPromoCountdown(formatCountdown(ms));

      const nextDailyStart = getNextDailyPromoStart(getNowVenezuela());
      setDailyPromoCountdown(formatCountdown(nextDailyStart - Date.now()));

      const nextThursdayStart = getNextThursdayPromoStart(getNowVenezuela());
      setThursdayPromoCountdown(formatCountdown(nextThursdayStart - Date.now()));
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (HOME_DISABLE_MOTION_DEBUG) return;

    const container = sectionsRef.current;
    if (!container) return;

    const sections = container.querySelectorAll(":scope > *");
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("premium-reveal");
            observer.unobserve(entry.target);
          }
        }
      },
      {threshold: 0.08}
    );

    for (const section of sections) {
      section.style.opacity = "0";
      section.style.transform = "translateY(24px)";
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      setStorage("hasModalBeenShown", false);
    }

    if (window.location.pathname === "/") {
      dropStorage("isMobileLoginUser");
    }
  }, [authStatus]);

  useEffect(() => {
    if (HOME_DISABLE_MOTION_DEBUG) return;

    const el = atmosphereRef.current;
    const zone = zoneRef.current;
    if (!el) return;

    let ticking = false;
    let lastZone = "";

    const getZone = progress => {
      if (progress < 0.15) return "hero";
      if (progress < 0.6) return "games";
      if (progress < 0.82) return "sports";
      return "footer";
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const clampedY = Math.min(Math.round(y), Math.max(max, 0));
        const progress = max > 0 ? Math.min(clampedY / max, 1) : 0;

        el.style.setProperty("--scroll-progress", progress.toFixed(4));
        el.style.setProperty("--parallax-y", String(clampedY));

        const currentZone = getZone(progress);
        if (zone && currentZone !== lastZone) {
          zone.dataset.zone = currentZone;
          lastZone = currentZone;
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, {passive: true});
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (HOME_DISABLE_MOTION_DEBUG) return;

    const container = sectionsRef.current;
    if (!container) return;

    const glowElements = container.querySelectorAll(`[data-glow]`);
    if (glowElements.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          entry.target.dataset.visible = entry.isIntersecting ? "true" : "false";
        }
      },
      {rootMargin: "100px 0px", threshold: 0.01}
    );

    for (const el of glowElements) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const filteredHomeGames = useMemo(() => {
    let games = allGames;

    if (homeActiveProvider) {
      games = games.filter(g => g.provider === homeActiveProvider);
    }
    if (homeActiveCategory) {
      games = games.filter(g => g.class === homeActiveCategory);
    }
    if (homeSearchTerm.length >= 2) {
      const term = homeSearchTerm.toLowerCase();
      games = games.filter(
        game => game.name.toLowerCase().includes(term) || game.provider.toLowerCase().includes(term)
      );
    }

    return games;
  }, [allGames, homeSearchTerm, homeActiveProvider, homeActiveCategory]);

  const isHomeSearching = homeSearchTerm.length >= 2 || homeActiveProvider || homeActiveCategory;
  const isMonday = getNowVenezuela().getDay() === 1;
  const veNow = getNowVenezuela();
  const isDailyPromoActive = veNow.getDay() !== 4 && veNow.getHours() >= 20 && veNow.getHours() < 21;
  const isThursdayPromoActive = veNow.getDay() === 4 && veNow.getHours() >= 20 && veNow.getHours() < 21;
  const countdownText = useMemo(() => {
    const {days, hours, minutes} = promoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [promoCountdown]);
  const dailyPromoCountdownText = useMemo(() => {
    const {days, hours, minutes} = dailyPromoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [dailyPromoCountdown]);
  const thursdayPromoCountdownText = useMemo(() => {
    const {days, hours, minutes} = thursdayPromoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [thursdayPromoCountdown]);

  const handleHomeSearch = useCallback(value => {
    setHomeSearchTerm(value);
  }, []);

  const handleHomeSearchSubmit = useCallback(
    value => {
      if (value.trim()) {
        setStoreSearch(value);
        navigate("/gameslist/all");
      }
    },
    [setStoreSearch, navigate]
  );

  const handleRegisterPromoClick = useCallback(() => {
    if (authStatus === "authenticated") {
      navigate("/deposit");
      return;
    }

    handleAuthForm("registration");
    handleShowAuthModal();
  }, [authStatus, handleAuthForm, handleShowAuthModal, navigate]);

  const handleOpenGame = useCallback(
    (gameId, provider) => {
      if (authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      navigate(`/games/${provider.toLowerCase()}/${gameId}`);
    },
    [authStatus, handleShowAuthModal, navigate]
  );

  const handleOk = () => {
    setIsModalOpen(false);
    navigate("/raffles");
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <SEO
        title="Aprieta y Gana - Casino, Deportes, Loterías y más"
        description="Aprieta y Gana - Tu plataforma de apuestas en línea. Casino, deportes, loterías, bingo, caballos y más. ¡Donde el ganador eres tú!"
        path="/"
      />
      {authStatus === "authenticated" && (
        <>
          <ModalSessionInfo />
          <PaymentProfileCard />
          <AppBanners />
          {isModalOpen && (
            <div
              className={styles.raffleModalOverlay}
              onClick={handleCancel}
              onKeyDown={e => e.key === "Escape" && handleCancel()}
              role="dialog"
              aria-modal="true"
              aria-label="Rifa Fin de Año"
            >
              <div
                className={styles.raffleModalContent}
                onClick={e => e.stopPropagation()}
              >
                <button
                  className={styles.raffleModalClose}
                  onClick={handleCancel}
                  aria-label="Cerrar"
                  type="button"
                >
                  &times;
                </button>
                <img
                  className={styles.raffleModalImage}
                  onClick={handleOk}
                  onKeyUp={e => {
                    if (e.key === "Enter" || e.key === " ") handleOk();
                  }}
                  alt="Rifa Fin de Año"
                  src="https://images.cdn.raspaygana.com.ve/raspaygana-rifa-diciembre2024-1.jpeg"
                />
                <PrimaryButton
                  onClick={handleOk}
                  className={styles.raffleModalBtn}
                >
                  Compra YA!
                </PrimaryButton>
              </div>
            </div>
          )}
        </>
      )}

      <div
        className={styles.homeAtmosphere}
        ref={atmosphereRef}
      >
        <div
          className={styles.atmosphereDeep}
          aria-hidden="true"
        />
        <div
          className={styles.atmosphereCanvas}
          aria-hidden="true"
        />
        <div
          className={styles.atmosphereHero}
          aria-hidden="true"
        />
        <div
          className={styles.atmosphereOrbs}
          aria-hidden="true"
        />
        <div
          className={styles.atmosphereZone}
          ref={zoneRef}
          data-zone="hero"
          aria-hidden="true"
        />
        <ErrorBoundaryHome>
          <div className={styles.heroArea}>
            <StoriesCarousel />

            <HeroBanner
              slides={[
                {
                  bgImage: bannerFeed,
                  content: (
                    <HeroContent key="slide-1">
                      <HeroTag
                        variant="promo"
                        label={t("homeHeroTagPromo")}
                      />
                      <HeroTitle>
                        {t("homeHeroTitle", {defaultValue: "Bienvenido a "})}
                        <HeroAccent>{t("homeHeroAccent", {defaultValue: "Aprieta y Gana"})}</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroSub", {
                          defaultValue: "Casino, deportes, loterías y más. ¡Donde el ganador eres tú!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaPlay", {defaultValue: "Jugar Ahora"})}
                        href="/games"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerEvoplay,
                  content: (
                    <HeroContent key="slide-2">
                      <HeroTag
                        variant="nuevo"
                        label={t("homeHeroTagNew")}
                      />
                      <HeroTitle>
                        <HeroAccent>Evoplay</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroSlide2Sub", {
                          defaultValue: "Miles de tragamonedas, apuestas deportivas, loterías y mucho más.",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaDiscover", {defaultValue: "Descubrir"})}
                        href="/games"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerCashback,
                  content: (
                    <HeroContent key="slide-3">
                      <HeroTag
                        variant="drop"
                        label={t("homeHeroTagDrop")}
                      />
                      <HeroTitle>
                        <HeroAccent>Cashback</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroCashbackSub", {
                          defaultValue: "Recupera parte de tus apuestas. ¡Tu dinero siempre rinde más!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaMore", {defaultValue: "Saber Más"})}
                        href="/promos"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerDosHoras,
                  content: (
                    <HeroContent key="slide-4">
                      <HeroTag
                        variant="race"
                        label={t("homeHeroTagRace")}
                      />
                      <HeroTitle>
                        <HeroAccent>{t("homeHeroDosHorasAccent", {defaultValue: "2 Horas Locas"})}</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroDosHorasSub", {
                          defaultValue: "Promociones exclusivas por tiempo limitado. ¡No te las pierdas!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaPlay", {defaultValue: "Jugar Ahora"})}
                        href="/games"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerMananas,
                  content: (
                    <HeroContent key="slide-5">
                      <HeroTag
                        variant="torneo"
                        label={t("homeHeroTagTournament")}
                      />
                      <HeroTitle>
                        <HeroAccent>{t("homeHeroMananasAccent", {defaultValue: "Promo Mañanas"})}</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroMananasSub", {
                          defaultValue: "Empieza el día ganando con nuestras promociones matutinas.",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaMore", {defaultValue: "Saber Más"})}
                        href="/promos"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerAnimalitos,
                  content: (
                    <HeroContent key="slide-6">
                      <HeroTag
                        variant="nuevo"
                        label={t("homeHeroTagNew")}
                      />
                      <HeroTitle>
                        <HeroAccent>Animalitos</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroAnimalitosSub", {
                          defaultValue: "La lotería de animalitos más popular. ¡Juega y gana al instante!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaPlay", {defaultValue: "Jugar Ahora"})}
                        href="/games"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerWildTiger,
                  content: (
                    <HeroContent key="slide-7">
                      <HeroTag
                        variant="drop"
                        label={t("homeHeroTagDrop")}
                      />
                      <HeroTitle>
                        <HeroAccent>Wild Tiger</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroWildTigerSub", {
                          defaultValue: "Descubre la emoción salvaje de Wild Tiger. ¡Giros que rugen!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaDiscover", {defaultValue: "Descubrir"})}
                        href="/games"
                      />
                    </HeroContent>
                  ),
                },
                {
                  bgImage: bannerZelle,
                  content: (
                    <HeroContent key="slide-8">
                      <HeroTag
                        variant="promo"
                        label={t("homeHeroTagPromo")}
                      />
                      <HeroTitle>
                        <HeroAccent>Zelle y Pago Móvil</HeroAccent>
                      </HeroTitle>
                      <HeroSubtitle>
                        {t("homeHeroZelleSub", {
                          defaultValue: "Deposita y retira fácil con Zelle y Pago Móvil. ¡Rápido y seguro!",
                        })}
                      </HeroSubtitle>
                      <HeroCta
                        label={t("homeHeroCtaDeposit", {defaultValue: "Depositar"})}
                        href="/deposit"
                      />
                    </HeroContent>
                  ),
                },
              ]}
            />

            <GameCategoriesCarousel />

            <GameSearchBar
              onSearch={handleHomeSearch}
              onSubmit={handleHomeSearchSubmit}
              placeholder={t("homeSearchPlaceholder", {defaultValue: "Buscar juegos, proveedores..."})}
              providerOptions={providerOptions}
              categoryOptions={classOptions}
              activeProvider={homeActiveProvider}
              activeCategory={homeActiveCategory}
              onProviderChange={setHomeActiveProvider}
              onCategoryChange={setHomeActiveCategory}
              resultCount={isHomeSearching ? filteredHomeGames.length : undefined}
            />

            {!isHomeSearching && <ThemesCarousel />}

            {isHomeSearching && (
              <div className={styles.homeSearchResults}>
                {filteredHomeGames.length > 0 ? (
                  <>
                    <div className={styles.homeSearchGrid}>
                      {filteredHomeGames.slice(0, 12).map(game => (
                        <GameCardHome
                          key={game.id}
                          game={game}
                          handleOpenGame={handleOpenGame}
                        />
                      ))}
                    </div>
                    {filteredHomeGames.length > 12 && (
                      <button
                        type="button"
                        className={styles.homeSearchSeeAll}
                        onClick={() => {
                          setStoreSearch(homeSearchTerm);
                          navigate("/gameslist/all");
                        }}
                      >
                        {t("seeAllResults", {
                          defaultValue: `Ver todos los resultados (${filteredHomeGames.length})`,
                        })}
                      </button>
                    )}
                  </>
                ) : (
                  <p className={styles.homeSearchEmpty}>
                    {t("noGamesFound", {defaultValue: "No se encontraron juegos"})}
                  </p>
                )}
              </div>
            )}

            {!isHomeSearching && <HomeNewsCarousel />}
          </div>

          <div
            className={styles.homeSections}
            ref={sectionsRef}
          >
            <BreakingLeague />

            <div className={styles.promoRow}>
              {authStatus === "unauthenticated" && (
                <PromoBanner
                  ariaLabel={t("registerPromoAria")}
                  onClick={handleRegisterPromoClick}
                  title={t("registerPromoTitle")}
                  description={t("registerPromoDesc")}
                  tagText={t("registerPromoTag")}
                  bottomValue={t("registerPromoCta")}
                  badgeMain="🎰"
                  themeStyle={registerPromoTheme}
                />
              )}

              {authStatus === "authenticated" && (
                <>
                  <PromoBanner
                    ariaLabel={t("cashbackBannerAria")}
                    title={t("cashbackTitle")}
                    description={t("cashbackDesc")}
                    tagText={isMonday ? t("cashbackPayday") : t("cashbackActive")}
                    bottomLabel={!isMonday ? t("cashbackNextMonday") : undefined}
                    bottomValue={!isMonday ? countdownText : undefined}
                    badgeMain={`${CASHBACK_PERCENTAGE}%`}
                    themeStyle={cashbackPromoTheme}
                  />
                  <PromoBanner
                    ariaLabel={t("mondaySpinsAria")}
                    title={t("mondaySpinsTitle")}
                    description={t("mondaySpinsDesc")}
                    tagText={!isMonday ? t("mondaySpinsTag") : undefined}
                    actionText={isMonday ? t("mondaySpinsDeposit") : undefined}
                    onActionClick={isMonday ? handleBankAccountModal : undefined}
                    bottomLabel={!isMonday ? t("mondaySpinsNextMonday") : undefined}
                    bottomValue={!isMonday ? countdownText : undefined}
                    badge={
                      <div className={styles.customMondayBadge}>
                        <span className={styles.customMondayBadgeMain}>40</span>
                        <span className={styles.customMondayBadgeSub}>VEs</span>
                      </div>
                    }
                    themeStyle={dailySpinsPinkTheme}
                  />
                  <PromoBanner
                    ariaLabel={t("mondaySpinsAria")}
                    title={t("dailySpinsTitle")}
                    description={t("dailySpinsDesc")}
                    tagText={isDailyPromoActive ? t("active") : t("dailySpinsTag")}
                    bottomLabel={!isDailyPromoActive ? t("dailySpinsNextActive") : undefined}
                    bottomValue={isDailyPromoActive ? t("active") : dailyPromoCountdownText}
                    badge={
                      <div className={styles.customMondayBadge}>
                        <span className={styles.customMondayBadgeMain}>1</span>
                        <span className={styles.customMondayBadgeSub}>Giro</span>
                      </div>
                    }
                    themeStyle={thursdaySpinsRedTheme}
                  />
                  <PromoBanner
                    ariaLabel={t("mondaySpinsAria")}
                    title={"Hora loca todos los jueves"}
                    description={
                      "Hora loca todos los jueves de 8 a 9 pm por cada 40 ves que deposites te damos un giro gratis."
                    }
                    tagText={isThursdayPromoActive ? t("active") : "Solo jueves 8:00pm - 9:00pm"}
                    bottomLabel={!isThursdayPromoActive ? "Próximo jueves activo en" : undefined}
                    bottomValue={isThursdayPromoActive ? t("active") : thursdayPromoCountdownText}
                    badge={
                      <div className={styles.customMondayBadge}>
                        <span className={styles.customMondayBadgeMain}>40</span>
                        <span className={styles.customMondayBadgeSub}>VEs</span>
                      </div>
                    }
                    themeStyle={mondaySpinsPromoTheme}
                  />
                </>
              )}
            </div>

            {authStatus === "authenticated" && <FavoriteGamesCarousel />}

            {authStatus === "authenticated" && <RecentlyPlayedGames />}

            <div style={{position: "relative"}}>
              <div
                className={styles.glowPromos}
                data-glow
                data-visible="false"
                aria-hidden="true"
              />
              <GamePromos />
            </div>

            <div style={{position: "relative"}}>
              <div
                className={styles.glowTopGames}
                data-glow
                data-visible="false"
                aria-hidden="true"
              />
              <TopGames />
            </div>

            <AnimalitosExpressCarousel />

            <div
              className={styles.homeStatsGrid}
              style={{padding: "0", margin: "16px 0"}}
            >
              <LiveWinners />
              <LiveBetFeed />
              <TopReferrals />
            </div>

            <MiniGamesCarousel />

            <div id="plinko-carousel">
              <ThemeGamesCarousel
                title="JUEGOS DE PLINKO"
                icon="🔴"
                filterFn={g => g.class?.toLowerCase() === "plinko" || g.name?.toLowerCase().includes("plinko")}
                searchTerm="plinko"
              />
            </div>
          </div>
        </ErrorBoundaryHome>
      </div>
    </>
  );
};

export default HomePage;
